import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';
import {
  Loader2, Plus, Edit2, Trash2, Shield, UserCog, CheckCircle2, XCircle, Key
} from 'lucide-react';

export const AdminAdmins = () => {
  const { token, admin: currentAdmin, hasPermission } = useAdminAuth();
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [passwordAdmin, setPasswordAdmin] = useState(null);
  
  // Form state
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('viewer');
  const [newPassword, setNewPassword] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const canEdit = hasPermission('admin:edit');

  const fetchAdmins = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/admins`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdmins(data.admins);
      }
    } catch (error) {
      console.error('Admins error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/roles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRoles(data.roles);
      }
    } catch (error) {
      console.error('Roles error:', error);
    }
  };

  useEffect(() => {
    fetchAdmins();
    fetchRoles();
  }, [token, backendUrl]);

  const resetForm = () => {
    setEmail('');
    setName('');
    setPassword('');
    setRole('viewer');
    setEditingAdmin(null);
  };

  const openEditDialog = (admin) => {
    setEditingAdmin(admin);
    setEmail(admin.email);
    setName(admin.name);
    setRole(admin.role);
    setPassword('');
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!email || !name) {
      toast.error('E-Mail und Name sind Pflichtfelder');
      return;
    }
    if (!editingAdmin && !password) {
      toast.error('Passwort ist für neue Admins erforderlich');
      return;
    }

    try {
      let response;
      if (editingAdmin) {
        response = await fetch(`${backendUrl}/api/admin/admins/${editingAdmin.id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ name, role })
        });
      } else {
        response = await fetch(`${backendUrl}/api/admin/admins`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email, name, password, role })
        });
      }

      if (response.ok) {
        toast.success(editingAdmin ? 'Admin aktualisiert' : 'Admin erstellt');
        setDialogOpen(false);
        resetForm();
        fetchAdmins();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen haben');
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/admin/admins/${passwordAdmin.id}/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: newPassword })
      });

      if (response.ok) {
        toast.success('Passwort geändert');
        setPasswordDialogOpen(false);
        setPasswordAdmin(null);
        setNewPassword('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler beim Ändern');
    }
  };

  const handleDelete = async (adminId) => {
    if (!confirm('Admin wirklich löschen?')) return;

    try {
      const response = await fetch(`${backendUrl}/api/admin/admins/${adminId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Admin gelöscht');
        fetchAdmins();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const toggleActive = async (admin) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/admins/${admin.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !admin.is_active })
      });
      if (response.ok) {
        toast.success(admin.is_active ? 'Admin deaktiviert' : 'Admin aktiviert');
        fetchAdmins();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler');
    }
  };

  const getRoleLabel = (roleName) => {
    const role = roles.find(r => r.name === roleName);
    return role?.label || roleName;
  };

  const getRoleColor = (roleName) => {
    const colors = {
      'super_admin': 'bg-purple-500/20 text-purple-400',
      'admin': 'bg-blue-500/20 text-blue-400',
      'support': 'bg-green-500/20 text-green-400',
      'viewer': 'bg-slate-500/20 text-slate-400'
    };
    return colors[roleName] || colors['viewer'];
  };

  return (
    <div className="space-y-6" data-testid="admin-admins">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Admin-Verwaltung</h1>
          <p className="text-slate-400 mt-1">{admins.length} Administratoren</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-2" /> Neuer Admin
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-800 border-slate-700">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingAdmin ? 'Admin bearbeiten' : 'Neuer Admin'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">E-Mail</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={!!editingAdmin}
                    placeholder="admin@wolkenhelden.de"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Name</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Max Mustermann"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                {!editingAdmin && (
                  <div className="space-y-2">
                    <Label className="text-slate-300">Passwort</Label>
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mindestens 8 Zeichen"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-slate-300">Rolle</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {roles.map((r) => (
                        <SelectItem key={r.name} value={r.name}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-600 text-slate-300">
                  Abbrechen
                </Button>
                <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700">
                  {editingAdmin ? 'Speichern' : 'Erstellen'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Passwort ändern</DialogTitle>
            <DialogDescription className="text-slate-400">
              Neues Passwort für {passwordAdmin?.name} setzen
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-slate-300">Neues Passwort</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Mindestens 8 Zeichen"
              className="bg-slate-700 border-slate-600 text-white mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)} className="border-slate-600 text-slate-300">
              Abbrechen
            </Button>
            <Button onClick={handleChangePassword} className="bg-blue-600 hover:bg-blue-700">
              Ändern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admins Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : admins.length === 0 ? (
            <p className="text-slate-500 text-center py-20">Keine Admins gefunden</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Admin</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Rolle</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Letzter Login</th>
                    {canEdit && (
                      <th className="text-right py-3 px-4 text-slate-400 font-medium">Aktionen</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => (
                    <tr key={admin.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-full bg-slate-700">
                            {admin.role === 'super_admin' ? (
                              <Shield className="h-5 w-5 text-purple-400" />
                            ) : (
                              <UserCog className="h-5 w-5 text-blue-400" />
                            )}
                          </div>
                          <div>
                            <span className="text-white font-medium block">{admin.name}</span>
                            <span className="text-slate-400 text-sm">{admin.email}</span>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <Badge className={getRoleColor(admin.role)}>
                          {getRoleLabel(admin.role)}
                        </Badge>
                      </td>
                      <td className="py-4 px-4">
                        {admin.is_active ? (
                          <Badge className="bg-green-500/20 text-green-400">
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Aktiv
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500/20 text-red-400">
                            <XCircle className="h-3 w-3 mr-1" /> Inaktiv
                          </Badge>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-slate-400 text-sm">
                          {admin.last_login
                            ? new Date(admin.last_login).toLocaleString('de-DE')
                            : 'Nie'
                          }
                        </span>
                      </td>
                      {canEdit && (
                        <td className="py-4 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setPasswordAdmin(admin); setPasswordDialogOpen(true); }}
                              className="text-orange-400 hover:text-orange-300"
                              title="Passwort ändern"
                            >
                              <Key className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(admin)}
                              className="text-blue-400 hover:text-blue-300"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            {admin.id !== currentAdmin.id && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleActive(admin)}
                                  className={admin.is_active ? 'text-yellow-400 hover:text-yellow-300' : 'text-green-400 hover:text-green-300'}
                                  title={admin.is_active ? 'Deaktivieren' : 'Aktivieren'}
                                >
                                  {admin.is_active ? <XCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(admin.id)}
                                  className="text-red-400 hover:text-red-300"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
